import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Bell, X, Trophy } from 'lucide-react';

export function NotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seen = useRef(false);

  const load = async () => {
    try {
      const r = await api.get('/notifications');
      setItems(r.data.notifications || []);
      setUnread(r.data.unread || 0);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const openPanel = async () => {
    setOpen(true);
    if (unread > 0) {
      try { await api.post('/notifications/read-all'); } catch { /* noop */ }
      setUnread(0);
    }
  };

  const clickItem = (n) => {
    setOpen(false);
    if (n.type === 'weekly_report') nav('/app/digest');
  };

  return (
    <div className="relative">
      <button onClick={openPanel} data-testid="notification-bell" className="relative w-10 h-10 rounded-full bg-[var(--c-film)] flex items-center justify-center active:scale-95 transition">
        <Bell className="w-5 h-5 text-foreground/80" />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF5C5C] text-[var(--c-ink)] text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-12 z-50 w-80 max-w-[85vw] regen-card-elevated p-0 overflow-hidden shadow-2xl" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--c-hair)]">
              <span className="font-display font-bold text-sm">Notifications</span>
              <button onClick={() => setOpen(false)} data-testid="notification-close"><X className="w-4 h-4 text-muted-foreground" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-[var(--c-hair)]">
              {items.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No notifications yet.</div>}
              {items.map(n => (
                <button key={n.id} onClick={() => clickItem(n)} data-testid={`notification-${n.id}`}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-[var(--c-film)] transition ${!n.read ? 'bg-[var(--c-green-soft)]' : ''}`}>
                  <div className="w-8 h-8 rounded-lg bg-[var(--c-green-soft)] flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-4 h-4 text-[var(--c-green-ink)]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-snug">{n.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{n.body}</div>
                    <div className="text-[10px] text-muted-foreground/60 mt-1">{(n.created_at || '').slice(0, 10)}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
